<template>
  <div class="flex flex-col h-full bg-white rounded-lg shadow w-full">
    <Toast />
    <div class="p-6 border-b ">
      <div class="flex justify-between items-center">
        <div class="flex items-center gap-4">
          <Button
            icon="pi pi-arrow-left"
            text
            @click="router.back()"
          />
          <router-link
            to="/paperwork/repair-tag"
            class="hover:opacity-80 transition-opacity"
          >
            <img
              src="/images/qrCode.png"
              alt="Asset QR Code"
              class="w-20 h-20 object-contain"
            >
          </router-link>
          <div>
            <h1 class="text-2xl font-semibold text-gray-800 flex items-center gap-2 mb-1">
              <router-link
                to="/wiki/item/7/guides"
                class="hover:text-primary transition-colors"
              >
                {{ repair?.name }}
              </router-link>
              <router-link
                to="/stock/0"
                class="text-xl text-gray-500 font-normal hover:text-primary transition-colors"
              >
                ({{ repair?.barcode }})
              </router-link>
            </h1>
            <div class="flex items-center gap-2 text-sm">
              <span class="text-gray-600">{{ repair?.department }}</span>
              <i class="pi pi-chevron-right text-gray-400 text-xs" />
              <span class="text-gray-600">{{ repair?.masterCategory || 'Equipment' }}</span>
              <i class="pi pi-chevron-right text-gray-400 text-xs" />
              <span class="text-gray-600">{{ repair?.category }}</span>
            </div>
          </div>
        </div>
        <div class="flex gap-2">
          <SplitButton
            label="Complete"
            icon="pi pi-check"
            :model="completeOptions"
            severity="secondary"
          />
        </div>
      </div>
    </div>

    <div class="p-6 flex-1 overflow-auto">
      <!-- Content -->
      <div class="w-full">
        <div class="space-y-6">
          <!-- Timeline - Now Horizontal -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <h3 class="text-lg font-semibold mb-4">
              Repair Status
            </h3>
            <div class="relative">
              <!-- Horizontal line -->
              <div class="absolute left-0 right-0 top-[15px] h-px bg-gray-200" />

              <!-- Timeline items -->
              <div class="flex justify-between relative">
                <div
                  v-for="(item, index) in repairHistory"
                  :key="index"
                  class="flex flex-col items-center relative z-10"
                >
                  <!-- Icon -->
                  <div class="w-[35px] h-[35px] flex items-center justify-center bg-white">
                    <i
                      :class="[
                        'pi flex items-center justify-center rounded-full text-center',
                        'text-[0.75rem] leading-[1rem] inline-flex',
                        item.completed
                          ? 'w-7 h-7 text-white'
                          : 'w-5 h-5 text-gray-300 border-2 border-gray-300 bg-white',
                        {
                          'pi-check bg-green-500': item.status === 'Return to Service' && item.completed,
                          'pi-wrench bg-blue-500': item.status === 'Item Repaired' && item.completed,
                          'pi-box bg-orange-500': item.status === 'Parts Ordered' && item.completed,
                          'pi-search bg-purple-500': item.status === 'Initial Look' && item.completed,
                          'pi-file bg-gray-500': item.status === 'Repair Logged' && item.completed
                        }
                      ]"
                    />
                  </div>

                  <!-- Content -->
                  <div class="flex flex-col items-center mt-2">
                    <span
                      :class="[
                        'text-sm font-medium text-center',
                        item.completed ? 'text-gray-700' : 'text-gray-400'
                      ]"
                    >{{ item.status }}</span>
                    <div
                      v-if="item.completed"
                      class="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600 mt-1"
                    >
                      {{ item.completedBy }}
                    </div>
                    <span class="text-xs text-gray-500 mt-1">{{ formatDate(item.date) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Problem Half -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <h3 class="text-lg font-semibold mb-4">
              Problem
            </h3>
            <div class="space-y-4">
              <div>
                <div class="flex items-center justify-between gap-2">
                  <label class="text-sm font-medium w-[100px]">Repair Date:</label>
                  <div class="text-gray-600">
                    {{ formatDate(repair.repairDate) }}
                  </div>
                </div>
                <div class="flex items-center justify-between gap-2">
                  <label class="text-sm font-medium w-[100px]">Repair ID:</label>
                  <div class="font-mono text-gray-600">
                    {{ repair.id }}
                  </div>
                </div>
              </div>
              <div>
                <Button
                  label="Fault Details"
                  icon="pi pi-external-link"
                  text
                  class="p-0"
                  @click="showFaultDialog = true"
                />
                <p class="text-gray-600">
                  {{ repair.faultDetails || repair.fault }}
                </p>
              </div>
              <div>
                <div class="flex items-center justify-between">
                  <label class="text-sm font-medium w-[100px]">Last Job:</label>
                  <div class="font-mono text-gray-600">
                    {{ repair.lastJobNumber }}
                  </div>
                </div>
                <Dropdown
                  v-model="repair.status"
                  :options="statusOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select Status"
                  class="w-[200px]"
                />
              </div>
            </div>
          </div>

          <!-- Solution Half -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <h3 class="text-lg font-semibold mb-4">
              Solution
            </h3>
            <div class="space-y-4">
              <div>
                <Button
                  label="Work Done"
                  icon="pi pi-external-link"
                  text
                  class="p-0"
                  @click="showWorkDialog = true"
                />
                <p class="text-gray-600">
                  {{ repair.workDone || '—' }}
                </p>
              </div>
              <div>
                <label class="text-sm text-gray-500 mb-1 block">Parts Cost</label>
                <div class="flex gap-2">
                  <InputNumber
                    v-model="repair.partsCost"
                    mode="currency"
                    currency="GBP"
                    locale="en-GB"
                    class="w-full"
                  />
                  <Button
                    label="Select Parts"
                    icon="pi pi-list"
                    @click="showPartsSelectDialog = true"
                  />
                </div>
                <!-- Selected Parts DataTable -->
                <div
                  v-if="selectedParts.length > 0"
                  class="mt-2 border border-gray-200 rounded-md"
                >
                  <div class="bg-gray-50 p-2 border-b border-gray-200">
                    <h4 class="text-sm font-medium">
                      Selected Parts ({{ selectedParts.length }})
                    </h4>
                  </div>
                  <DataTable
                    :value="selectedParts"
                    class="p-2"
                    striped-rows
                    edit-mode="cell"
                    @cell-edit-complete="onSelectedPartCellEdit"
                  >
                    <Column
                      field="partNumber"
                      header="Part Number"
                    />
                    <Column
                      field="name"
                      header="Name"
                    />
                    <!-- Qty column -->
                    <Column
                      header="Qty"
                      style="width: 100px"
                    >
                      <template #body="slotProps">
                        <InputNumber
                          v-model="slotProps.data.qty"
                          :min="1"
                          input-style="width: 3rem"
                          show-buttons
                          button-layout="horizontal"
                          @change="updateTotalPartsCost"
                        />
                      </template>
                    </Column>
                    <!-- Price column (editable) -->
                    <Column
                      field="price"
                      header="Price"
                      :editor="true"
                      style="width: 150px"
                    >
                      <template #body="slotProps">
                        <span @dblclick="onPriceDoubleClick(slotProps.data)">
                          £{{ slotProps.data.price.toFixed(2) }}
                        </span>
                      </template>
                      <template #editor="{ data, field }">
                        <InputNumber
                          v-model="data[field]"
                          mode="currency"
                          currency="GBP"
                          locale="en-GB"
                          input-style="width: 6rem"
                          autofocus
                        />
                      </template>
                    </Column>
                    <Column
                      header="Actions"
                      style="width: 80px"
                    >
                      <template #body="slotProps">
                        <Button
                          icon="pi pi-trash"
                          text
                          severity="danger"
                          class="p-button-sm"
                          @click="removePart(selectedParts.indexOf(slotProps.data))"
                        />
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>
              <div>
                <label class="text-sm text-gray-500 mb-1 block">Labour Cost</label>
                <InputNumber
                  v-model="repair.labourCost"
                  mode="currency"
                  currency="GBP"
                  locale="en-GB"
                  class="w-full"
                />
              </div>
              <div>
                <label class="text-sm text-gray-500 mb-1 block">Charge Value</label>
                <InputNumber
                  v-model="repair.chargeValue"
                  mode="currency"
                  currency="GBP"
                  locale="en-GB"
                  class="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="showFaultDialog"
    modal
    header="Fault Details"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '75vw', '641px': '90vw' }"
  >
    <div class="space-y-4">
      <label class="text-sm text-gray-500">Enter Fault Details</label>
      <Textarea
        v-model="repair.faultDetails"
        rows="5"
        class="w-full"
        auto-resize
      />
    </div>
    <template #footer>
      <Button
        label="Close"
        icon="pi pi-times"
        text
        @click="showFaultDialog = false"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        severity="secondary"
        @click="showFaultDialog = false"
      />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showWorkDialog"
    modal
    header="Work Done"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '75vw', '641px': '90vw' }"
  >
    <div class="space-y-4">
      <label class="text-sm text-gray-500">Enter Work Done</label>
      <Textarea
        v-model="repair.workDone"
        rows="5"
        class="w-full"
        auto-resize
      />
    </div>
    <template #footer>
      <Button
        label="Close"
        icon="pi pi-times"
        text
        @click="showWorkDialog = false"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        severity="secondary"
        @click="showWorkDialog = false"
      />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showPartsSelectDialog"
    modal
    header="Select Parts"
    :style="{ width: '90vw' }"
    :breakpoints="{ '960px': '75vw', '641px': '90vw' }"
  >
    <div class="space-y-4">
      <h3 class="text-lg font-semibold">
        Available Parts
      </h3>
      <DataTable
        v-model:selection="selectedParts"
        :value="availableParts"
        data-key="partNumber"
        :paginator="true"
        :rows="10"
        class="p-2"
        :filters="filters"
        filter-display="menu"
      >
        <!-- Checkbox select column -->
        <Column
          selection-mode="multiple"
          header-style="width: 3rem"
        />

        <!-- Filterable columns -->
        <Column
          field="partNumber"
          header="Part Number"
          :show-filter-menu="false"
          :filter="true"
          filter-placeholder="Search Part Number"
          style="min-width: 10rem"
        >
          <template #filter>
            <InputText
              v-model="filters['partNumber'].value" 
              class="p-inputtext-sm w-full"
              placeholder="Search Part Number"
            />
          </template>
        </Column>
        <Column
          field="name"
          header="Name"
          :show-filter-menu="false"
          :filter="true"
          filter-placeholder="Search Name"
          style="min-width: 10rem"
        >
          <template #filter>
            <InputText
              v-model="filters['name'].value" 
              class="p-inputtext-sm w-full"
              placeholder="Search Name"
            />
          </template>
        </Column>
        <Column
          field="manufacturer"
          header="Manufacturer"
          :show-filter-menu="false"
          :filter="true"
          filter-placeholder="Search Manufacturer"
          style="min-width: 10rem"
        >
          <template #filter>
            <InputText
              v-model="filters['manufacturer'].value" 
              class="p-inputtext-sm w-full"
              placeholder="Search Manufacturer"
            />
          </template>
        </Column>

        <!-- Other display columns -->
        <Column
          field="stock"
          header="Stock"
        />

        <!-- Qty input column -->
        <Column header="Qty">
          <template #body="slotProps">
            <InputNumber
              v-model="slotProps.data.qty"
              :min="1"
              input-style="width: 3rem"
              show-buttons
              button-layout="horizontal"
            />
          </template>
        </Column>
        <!-- Price column -->
        <Column
          field="price"
          header="Price"
          :editor="true"
        >
          <template #body="slotProps">
            <InputNumber
              v-model="slotProps.data.price"
              mode="currency"
              currency="GBP"
              locale="en-GB"
              input-style="width: 6rem"
            />
          </template>
        </Column>

        <Column
          field="location"
          header="Location"
        />
      </DataTable>
    </div>
    <template #footer>
      <Button
        label="Close"
        icon="pi pi-times"
        text
        @click="showPartsSelectDialog = false"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        severity="secondary"
        @click="updatePartsCost"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToastService } from '@/composables/useToastService';

const router = useRouter();
const toast = useToastService();
const filters = ref({
    partNumber: { value: null, matchMode: 'contains' },
    name: { value: null, matchMode: 'contains' },
    manufacturer: { value: null, matchMode: 'contains' }
});
const repair = ref({
    id: '',
    name: '',
    barcode: '',
    department: '',
    masterCategory: '',
    category: '',
    equipmentValue: 0,
    fault: '',
    status: '',
    lastJob: null,
    // Simple view fields
    repairDate: new Date().toISOString().split('T')[0],
    lastJobNumber: '',
    partsCost: 0,
    labourCost: 0,
    chargeValue: 0,
    faultDetails: '',
    workDone: '',
    // Detailed view fields
    partsWork: '',
    labourWork: ''
});

// Dialog controls
const showFaultDialog = ref(false);
const showWorkDialog = ref(false);
const showPartsSelectDialog = ref(false);

// Parts selection state
const availableParts = ref([
    { partNumber: 'P001', name: 'Motherboard', manufacturer: 'Intel', stock: 5, price: 150.00, location: 'A1', qty: 0 },
    { partNumber: 'P002', name: 'RAM Module 8GB', manufacturer: 'Crucial', stock: 12, price: 45.99, location: 'A2', qty: 0 },
    { partNumber: 'P003', name: 'SSD 500GB', manufacturer: 'Samsung', stock: 8, price: 89.99, location: 'B1', qty: 0 },
    { partNumber: 'P004', name: 'Power Supply 650W', manufacturer: 'Corsair', stock: 4, price: 75.50, location: 'B3', qty: 0 },
    { partNumber: 'P005', name: 'Graphics Card', manufacturer: 'NVIDIA', stock: 2, price: 350.00, location: 'C1', qty: 0 }
]);

const selectedParts = ref([]);

// Add a part to the selected parts list


// Remove part from the selected parts list
const removePart = (index) => {
    const removedPart = selectedParts.value[index];
    selectedParts.value.splice(index, 1);

    // Recalculate total parts cost
    updatePartsCost();

    toast.add({
        severity: 'info',
        summary: 'Part Removed',
        detail: `${removedPart.name} removed from repair`,
        life: 3000
    });
};

// Add or update selected parts cell editing functions after updatePartsCost function
const updatePartsCost = () => {
    let totalCost = 0;
    selectedParts.value.forEach(part => {
        totalCost += part.price * (part.qty || 1);
    });

    // Update parts cost in the repair object
    repair.value.partsCost = totalCost;

    showPartsSelectDialog.value = false;

    toast.add({
        severity: 'success',
        summary: 'Parts Cost Updated',
        detail: `Total parts cost: £${totalCost.toFixed(2)}`,
        life: 3000
    });
};

// Handle cell edit completion for selected parts
const onSelectedPartCellEdit = (event) => {
    const { data, newValue, field } = event;

    if (field === 'price') {
        // Validate price
        if (newValue < 0) {
            toast.add({
                severity: 'error',
                summary: 'Invalid Price',
                detail: 'Price cannot be negative',
                life: 3000
            });
            // Reset to original price or set to 0
            data[field] = Math.max(0, data[field]);
        } else {
            toast.add({
                severity: 'success',
                summary: 'Price Updated',
                detail: `${data.name} price updated to £${newValue.toFixed(2)}`,
                life: 3000
            });
            // Recalculate total cost when price changes
            updateTotalPartsCost();
        }
    }
};

// Function to update the total parts cost without closing the dialog
const updateTotalPartsCost = () => {
    let totalCost = 0;
    selectedParts.value.forEach(part => {
        // Consider quantity when calculating cost
        totalCost += part.price * (part.qty || 1);
    });

    // Update parts cost in the repair object
    repair.value.partsCost = totalCost;
};

// Function for price double click event if needed
const onPriceDoubleClick = () => {
    // This is handled by the cell editing functionality
    // This function is here if you want to add additional logic when a cell is double-clicked
};

// Status options
const statusOptions = [
    { label: 'Repair Logged', value: 'logged' },
    { label: 'Initial Look', value: 'inProgress' },
    { label: 'Parts Ordered', value: 'partsOrdered' },
    { label: 'Item Repaired', value: 'repaired' },
    { label: 'Return to Service', value: 'returnToService' }
];

// Complete options for split button
const completeOptions = [
    { label: 'Return to Service', icon: 'pi pi-check', command: () => completeRepair('returnToService') },
    { label: 'Beyond Economic Repair', icon: 'pi pi-times', command: () => completeRepair('BER') }
];

// Mock repair history
const repairHistory = [
    { status: 'Repair Logged', date: new Date('2023-09-15'), completed: true, completedBy: 'AJ' },
    { status: 'Initial Look', date: new Date('2023-09-16'), completed: true, completedBy: 'MK' },
    { status: 'Parts Ordered', date: new Date('2023-09-18'), completed: false },
    { status: 'Item Repaired', date: new Date(), completed: false },
    { status: 'Return to Service', date: new Date(), completed: false }
];

// Format date
const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Complete repair
const completeRepair = (status) => {
    repair.value.status = status;
    toast.add({
        severity: 'success',
        summary: 'Repair Updated',
        detail: `Repair marked as ${status}`,
        life: 3000
    });
};

// Fetch repair data
onMounted(() => {
    // In a real app, we would fetch data from an API
    // For now, we're using mock data
    repair.value = {
        id: 'REP-' + Math.floor(Math.random() * 10000),
        name: 'Dell XPS 15 Laptop',
        barcode: 'ASSET-1234',
        department: 'IT Department',
        masterCategory: 'Computing',
        category: 'Laptops',
        equipmentValue: 1200,
        fault: 'Will not power on',
        status: 'inProgress',
        lastJob: null,
        repairDate: new Date().toISOString().split('T')[0],
        lastJobNumber: 'JOB-5678',
        partsCost: 0,
        labourCost: 0,
        chargeValue: 0,
        faultDetails: 'Device will not power on. No lights, no fan noise, no response when power button is pressed.',
        workDone: '',
        partsWork: '',
        labourWork: ''
    };
});
</script>
